import { useEffect, useRef } from "react";

/**
 * Pointer-reactive node field.
 * variant="fixed"  → viewport-wide layer behind the whole page (default)
 * variant="local"  → fills its parent element (used inside dark sections)
 */
export default function SignalField({
  variant = "fixed",
  rgb = "68,58,210",
  opacity,
  testId = "signal-field",
}) {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const local = variant === "local";
    const host = local ? canvas.parentElement : null;

    let width = 0;
    let height = 0;
    let frame = 0;
    let animation = 0;
    let nodes = [];
    const pointer = { x: 0, y: 0, active: false };

    const seedNodes = () => {
      const count = Math.max(26, Math.min(46, Math.round(width / 31)));
      nodes = Array.from({ length: count }, (_, i) => ({
        x: ((((i * 97) % count) + 0.5) / count) * width,
        y: ((((i * 53) % count) + 0.2) / count) * height,
        vx: Math.sin(i * 2.1) * 0.1,
        vy: Math.cos(i * 1.7) * 0.08,
        r: i % 7 === 0 ? 2.1 : 1.15,
      }));
    };

    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      for (const n of nodes) {
        if (!reduced) {
          n.x += n.vx;
          n.y += n.vy;
          if (n.x < 0 || n.x > width) n.vx *= -1;
          if (n.y < 0 || n.y > height) n.vy *= -1;
        }
        if (pointer.active) {
          const dx = n.x - pointer.x;
          const dy = n.y - pointer.y;
          const dist = Math.hypot(dx, dy) || 1;
          if (dist < 175) {
            const force = (175 - dist) / 175;
            n.x += (dx / dist) * force * 1.3;
            n.y += (dy / dist) * force * 1.3;
          }
        }
      }
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i];
          const b = nodes[j];
          const dist = Math.hypot(a.x - b.x, a.y - b.y);
          if (dist < 165) {
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.strokeStyle = `rgba(${rgb},${(1 - dist / 165) * 0.22})`;
            ctx.lineWidth = 0.75;
            ctx.stroke();
          }
        }
      }
      for (const n of nodes) {
        const near = pointer.active
          ? Math.max(0, 1 - Math.hypot(n.x - pointer.x, n.y - pointer.y) / 200)
          : 0;
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r + near * 1.5, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${rgb},${0.28 + near * 0.66})`;
        ctx.fill();
      }
      if (pointer.active) {
        const radius = 55 + Math.sin(frame * 0.04) * 3;
        const glow = ctx.createRadialGradient(pointer.x, pointer.y, 0, pointer.x, pointer.y, radius);
        glow.addColorStop(0, `rgba(${rgb},.19)`);
        glow.addColorStop(1, `rgba(${rgb},0)`);
        ctx.fillStyle = glow;
        ctx.beginPath();
        ctx.arc(pointer.x, pointer.y, radius, 0, Math.PI * 2);
        ctx.fill();
      }
      frame++;
      if (!reduced) animation = requestAnimationFrame(draw);
    };

    const resize = () => {
      if (local) {
        const r = host.getBoundingClientRect();
        width = Math.max(1, Math.round(r.width));
        height = Math.max(1, Math.round(r.height));
      } else {
        width = Math.max(1, window.innerWidth);
        height = Math.max(1, window.innerHeight);
      }
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      seedNodes();
      if (animation) cancelAnimationFrame(animation);
      draw();
    };

    const onMove = (e) => {
      if (local) {
        const r = canvas.getBoundingClientRect();
        pointer.x = e.clientX - r.left;
        pointer.y = e.clientY - r.top;
      } else {
        pointer.x = e.clientX;
        pointer.y = e.clientY;
      }
      pointer.active = true;
      if (reduced) draw();
    };
    const onOut = (e) => {
      if (e.relatedTarget) return;
      pointer.active = false;
      if (reduced) draw();
    };

    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("mouseout", onOut, { passive: true });
    window.addEventListener("resize", resize, { passive: true });

    let observer;
    if (local) {
      observer = new ResizeObserver(resize);
      observer.observe(host);
    }
    resize();

    return () => {
      if (animation) cancelAnimationFrame(animation);
      if (observer) observer.disconnect();
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("mouseout", onOut);
      window.removeEventListener("resize", resize);
    };
  }, [variant, rgb]);

  return (
    <canvas
      ref={ref}
      className={local_class(variant)}
      id={variant === "fixed" ? "signal-field" : undefined}
      data-testid={testId}
      aria-hidden="true"
      style={opacity !== undefined ? { opacity } : undefined}
    />
  );
}

const local_class = (variant) => (variant === "local" ? "signal-field-local" : "signal-field");
